// 3rd party imports
import * as React from 'react';
import { Link } from 'react-router-dom';
import { inject, observer } from 'mobx-react';

// bernie
import { ContextStore, RouteStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

// shared-ui
import { ILocalization, LocalizedText } from '@shared-ui/localization-context';

// mobx
import { Product, UserInfo, Program } from 'models';
import { ProductStore, ProgramStore, UserAuthStore } from 'stores';

// ui
import { UitkCardPlaceholder } from 'uitk-react-cards';
import { UitkHeading, UitkText } from 'uitk-react-text';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkBanner } from 'uitk-react-banner';
import { UitkLink } from 'uitk-react-link';
import { UitkPrimaryButton } from 'uitk-react-button';

// components
import { ProductCard } from './product-card';
import { JoinAffiliateNetworkDialog } from '../programs/join-affiliate-network-dialog';

// util
import { modifyLocaleParam } from '../../util/LocaleParamTransformer';

export interface ProductProps extends LocalizedComponentProps {
  context?: ContextStore;
  l10n?: ILocalization;
  products?: ProductStore;
  routeStore?: RouteStore;
  userAuth?: UserAuthStore;
  userInfo?: UserInfo;
  programs?: ProgramStore;
}

export interface ProductState {
  products: Product[];
  program: Program;
}

@withLocalization
@inject('context', 'products', 'routeStore', 'userAuth', 'programs')
@observer
export class Products extends React.Component<ProductProps, ProductState> {
  private params;

  public constructor(props: ProductProps) {
    super(props);

    this.params = null;

    this.state = {
      products: [],
      program: {
        name: '',
        title: '',
        brand: '',
        features: [],
        img: '',
        lobs: [],
        network: '',
        posa: '',
        locale: '',
        signInLink: '',
        signUpLink: 'string',
        commissionPercentage: 0,
        cookieDurationDays: 0,
      },
    };
  }

  public componentDidMount() {
    const { routeStore, programs, products } = this.props;
    this.params = routeStore.queryParams.programName;
    this.setState({
      products: products.getProducts(this.params),
      program: programs.getProgram(this.params),
    });
  }

  public componentDidUpdate() {
    const { routeStore, programs, userAuth, userInfo } = this.props;
    this.params = routeStore.queryParams.programName;

    const program = programs.getProgram(this.params);
    const programStatus = this.getProgramStatus(program, userInfo, userAuth);

    // Preview mode: for New or UNKNOWN state only
    if (programStatus === 'UNKNOWN' || programStatus === null) {
      routeStore.updateSearch({ pwaDialog: 'joinAffiliateNetwork-preview' });
    }
  }

  private getProgramStatus(program, userInfo, userAuth) {
    let programStatus = null;
    const userId = userInfo && userInfo.email ? userInfo.email : null;

    if (userId) {
      const userProgram = userAuth.userProgramRelationships
        ? userAuth.userProgramRelationships.body.filter((userProg) => {
            return (
              userProg.id.userId === userId && userProg.id.brand === program.brand && userProg.id.posa === program.posa
            );
          })
        : [];

      if (userProgram.length) {
        programStatus = userProgram[0].affiliatePartnership.partnershipStatus;
      }
    }

    return programStatus;
  }

  private renderHeaderBanner(programStatus) {
    const { l10n, routeStore } = this.props;
    const { program } = this.state;

    const productProgramTitle = (
      <>
        <LocalizedText message="products.text" />
        &nbsp;&ndash;&nbsp;
        {program.title}
      </>
    );

    const onClick = () => {
      routeStore.updateSearch({ pwaDialog: 'joinAffiliateNetwork-' + program.name });
    };

    // Preview mode: for New or UNKNOWN state only
    if (programStatus === null || programStatus === 'UNKNOWN') {
      const currentLocale = modifyLocaleParam(this.props.context.locale);
      const linkTo =
        program.network === 'PARTNERIZE'
          ? program.signUpLink.replace(':localeName', currentLocale)
          : { search: `pwaDialog=joinAffiliateNetwork-${program.name}` };

      return (
        <>
          <UitkBanner icon="info_outline" message={l10n.formatText('preview.products.banner.text')}>
            <UitkLink inline={true}>
              <Link to={linkTo} className="a--no-hover">
                <LocalizedText message="preview.products.join.the.program" />
              </Link>
            </UitkLink>
          </UitkBanner>

          <UitkSpacing margin={{ blockstart: 'four' }}>
            <UitkLayoutGrid columns={2} space="two">
              <>
                <UitkLayoutGridItem>{productProgramTitle}</UitkLayoutGridItem>
                <UitkLayoutGridItem alignSelf="end" justifySelf="end">
                  <Link to={linkTo}>
                    <UitkPrimaryButton type={'button'} text={l10n.formatText('preview.products.join.the.program')} />
                  </Link>
                </UitkLayoutGridItem>
              </>
            </UitkLayoutGrid>
          </UitkSpacing>
        </>
      );
    }

    // Pending state
    if (programStatus && programStatus === 'PENDING') {
      return (
        <>
          <UitkBanner icon="info_outline" message={l10n.formatText('products.pending.banner.text')} />
          <UitkText spacing="four">{productProgramTitle}</UitkText>
        </>
      );
    }

    return productProgramTitle;
  }

  public render() {
    const { l10n, routeStore, context, userAuth, userInfo } = this.props;
    const { products, program } = this.state;

    const cards = [];
    const programStatus = this.getProgramStatus(program, userInfo, userAuth);
    const currentLocale = modifyLocaleParam(context.locale);

    products.map((product) => {
      const isPreview =
        programStatus === null || (programStatus && (programStatus === 'UNKNOWN' || programStatus === 'PENDING'));
      if (product.link) {
        product.link = product.link.replace(':localeName', currentLocale);
      }
      cards.push(
        <UitkLayoutGridItem key={product.name}>
          <ProductCard
            locale={currentLocale}
            actions={true}
            l10n={l10n}
            product={product}
            program={program}
            pending={programStatus === 'PENDING'}
            preview={isPreview}
          />
        </UitkLayoutGridItem>,
      );
    });

    return (
      <>
        <UitkSpacing className="container" padding={{ block: 'twelve' }}>
          <main>
            <UitkHeading tag="h1" size={3}>
              {this.renderHeaderBanner(programStatus)}
            </UitkHeading>
            <UitkSpacing padding={{ blockstart: 'six' }}>
              <UitkLayoutGrid columns={{ small: 1, large: 2, extra_large: 3 }} space="four">
                {products.length === 0 &&
                  [...Array(9)].map((val, index) => (
                    <div key={index}>
                      <UitkCardPlaceholder aspectRatio="r1-1" />
                    </div>
                  ))}

                {cards}
              </UitkLayoutGrid>
            </UitkSpacing>
          </main>
        </UitkSpacing>
        {(programStatus === null || programStatus === 'UNKNOWN') && (
          <>
            <JoinAffiliateNetworkDialog
              dialogId={`joinAffiliateNetwork-${program.name}`}
              l10n={l10n}
              routeStore={routeStore}
              locale={context.locale}
              isPreviewMode={false}
              showManageProgramLink={program.network === 'CJ'}
              program={program}
            />
            <JoinAffiliateNetworkDialog
              dialogId={`joinAffiliateNetwork-preview`}
              l10n={l10n}
              routeStore={routeStore}
              locale={context.locale}
              isPreviewMode={true}
              showManageProgramLink={program.network === 'CJ'}
              program={program}
            />
          </>
        )}
      </>
    );
  }
}
